// =============================================================================
// src/views/workspace/workspace-undo-toast.ts — Toast com ação "Desfazer".
//
// Toast PRÓPRIO do Workspace (não toca no MiniToast de paridade) para confirmar
//   exclusão e "Restaurar exemplos" sem usar confirm() nativo (que bloquearia a
//   thread). Reaproveita um único nó no DOM; clicar "Desfazer" chama o callback e
//   esconde; o timer esconde silenciosamente (sem desfazer).
//
// SRP: só o ciclo mostrar/desfazer/esconder do toast. Estilos em inline-overrides.css.
// =============================================================================

/** Id do nó único do toast no DOM. */
const UNDO_TOAST_ID = '__workspaceUndoToast';
/** Tempo visível antes de auto-esconder, em ms. */
const UNDO_VISIBLE_MS = 5000;

/** Toast efêmero com rótulo + botão Desfazer. */
export class WorkspaceUndoToast {
  private element: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private onUndo: (() => void) | null = null;

  /** Mostra `message` com um botão que dispara `onUndo`; reinicia o timer. */
  show(message: string, onUndo: () => void): void {
    this.onUndo = onUndo;
    const toast = this.ensureElement();
    this.fill(toast, message);
    toast.classList.add('visible');
    this.restartTimer(toast);
  }

  /** Cria (uma vez) o nó do toast e o anexa ao body. */
  private ensureElement(): HTMLElement {
    if (this.element) return this.element;
    const created = document.createElement('div');
    created.id = UNDO_TOAST_ID;
    created.className = 'u-ws-undo-toast';
    document.body.appendChild(created);
    this.element = created;
    return created;
  }

  /** Preenche rótulo + botão Desfazer. */
  private fill(toast: HTMLElement, message: string): void {
    toast.replaceChildren(this.makeLabel(message), this.makeUndoButton(toast));
  }

  /** Span de texto do toast. */
  private makeLabel(message: string): HTMLElement {
    const label = document.createElement('span');
    label.className = 'u-ws-undo-label';
    label.textContent = message;
    return label;
  }

  /** Botão "Desfazer" que aciona o callback e fecha. */
  private makeUndoButton(toast: HTMLElement): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'u-ws-undo-btn';
    button.textContent = 'Desfazer';
    button.addEventListener('click', () => this.triggerUndo(toast));
    return button;
  }

  /** Esconde e, se houver, dispara o callback de desfazer. */
  private triggerUndo(toast: HTMLElement): void {
    const callback = this.onUndo;
    this.hide(toast);
    if (callback) callback();
  }

  /** Garante um único timer ativo de auto-esconder. */
  private restartTimer(toast: HTMLElement): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.hide(toast), UNDO_VISIBLE_MS);
  }

  /** Esconde o toast e zera o callback pendente. */
  private hide(toast: HTMLElement): void {
    toast.classList.remove('visible');
    this.onUndo = null;
  }
}
